/**
 * Aba "Metricas": dois graficos que a empresa entende de primeira.
 *
 *  - Barras por dia (ou por semana em janela longa): quem entrou na regua x
 *    quem o funil recuperou. Duas series, cores `series-1` e `series-2` na
 *    ordem, legenda sempre presente.
 *  - Aneis por canal: receita e quantidade. Os slots de cor por canal sao
 *    fixos (`CANAL[c].serie`) para a ligacao ser sempre a mesma cor em toda
 *    tela do modulo.
 *
 * O "ticket medio" e o unico numero derivado por divisao aqui e e SO de
 * exibicao (inteiro de centavos arredondado); nunca volta para uma conta.
 *
 * "Recuperado" leva a dica "sem o funil, teria ficado na mesa": e o "o que
 * teria perdido" que o dono pediu para o lojista ver -- retorno, nunca gasto.
 */

import { BarChart, DonutChart, SectionCard, StatCell, type DonutSlice } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact } from '../../../lib/format';
import { agruparSerie } from '../agregacao';
import { CREDIT_CHANNELS, type FunnelKind, type OfferMetrics } from '../types';
import { CANAL, VOCABULARIO } from '../vocabulario';

export function MetricasDoFunil({ kind, metrics }: { readonly kind: FunnelKind; readonly metrics: OfferMetrics }): JSX.Element {
  const v = VOCABULARIO[kind];
  const serie = agruparSerie(metrics.daily);
  const agrupado = metrics.daily.length > 45;

  const fatiasDeReceita: DonutSlice[] = CREDIT_CHANNELS.filter((c) => metrics.recoveredAmountByChannelCents[c] > 0).map((c) => ({
    key: c,
    label: CANAL[c].label,
    value: metrics.recoveredAmountByChannelCents[c],
    tone: CANAL[c].serie,
    display: formatBRLFromCents(metrics.recoveredAmountByChannelCents[c]),
  }));
  const fatiasDeQuantidade: DonutSlice[] = CREDIT_CHANNELS.filter((c) => metrics.recoveredByChannel[c] > 0).map((c) => ({
    key: c,
    label: CANAL[c].label,
    value: metrics.recoveredByChannel[c],
    tone: CANAL[c].serie,
    display: formatCount(metrics.recoveredByChannel[c]),
  }));

  const totalComAnteriores = metrics.recoveredAmountCents + metrics.carryOverAmountCents;
  const ticketMedio = metrics.recovered === 0 ? null : Math.round(metrics.recoveredAmountCents / metrics.recovered);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <SectionCard
        className="lg:col-span-2"
        title={agrupado ? 'Por semana' : 'Por dia'}
        description={`${v.entradaKpi} x ${v.recuperadosKpi.toLowerCase()} pelo funil, ${agrupado ? 'semana a semana' : 'dia a dia'}, na janela do cabeçalho.`}
        icon="grafico"
      >
        {serie.labels.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-subtle">Sem dias na janela para desenhar.</p>
        ) : (
          <BarChart
            title={`${v.entradaKpi} e ${v.recuperadosKpi.toLowerCase()} por ${agrupado ? 'semana' : 'dia'}`}
            labels={serie.labels}
            series={[
              { key: 'entered', label: v.entradaKpi, values: serie.entered, tone: 1 },
              { key: 'recovered', label: v.recuperadosKpi, values: serie.recovered, tone: 2 },
            ]}
            formatValue={formatCount}
            height={220}
          />
        )}
      </SectionCard>

      <SectionCard title="Receita recuperada por canal" description="A quem o último contato foi creditado (ADR-001)." icon="moeda">
        {fatiasDeReceita.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-subtle">Nenhuma recuperação no recorte.</p>
        ) : (
          <DonutChart slices={fatiasDeReceita} title="Receita recuperada por canal" center={{ value: formatCountCompact(metrics.recovered), label: 'recuperados' }} />
        )}
      </SectionCard>

      <SectionCard title={`${v.recuperadosKpi} por canal`} description="Quantidade de pessoas, pelo canal do último contato." icon="funil">
        {fatiasDeQuantidade.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-subtle">Nenhuma recuperação no recorte.</p>
        ) : (
          <DonutChart slices={fatiasDeQuantidade} title={`${v.recuperadosKpi} por canal`} center={{ value: formatCountCompact(metrics.recovered), label: 'pessoas' }} />
        )}
      </SectionCard>

      <SectionCard className="lg:col-span-2" title="Retorno do período" description="O que ficou para a empresa depois da comissão do Converte Fácil." icon="comissao">
        <div className="grid grid-cols-2 divide-x divide-line sm:grid-cols-3 lg:grid-cols-6">
          <StatCell label="Recuperado" value={formatBRLFromCents(totalComAnteriores)} hint={metrics.carryOverAmountCents > 0 ? 'inclui entradas anteriores · sem o funil, teria ficado na mesa' : 'sem o funil, teria ficado na mesa'} />
          <StatCell label="Comissão" value={formatBRLFromCents(metrics.commissionCents)} hint="a definir: contrato x automático" />
          <StatCell label="Líquido" value={formatBRLFromCents(totalComAnteriores - metrics.commissionCents)} tone="good" />
          <StatCell label="Ticket médio" value={ticketMedio === null ? '—' : formatBRLFromCents(ticketMedio)} />
          <StatCell label="Com o comercial" value={formatCount(metrics.withSeller)} tone="violet" hint="responderam, sem fechar ainda" />
          <StatCell label="Pediram pra sair" value={formatCount(metrics.unsubscribed)} tone="warning" />
        </div>
      </SectionCard>
    </div>
  );
}
